import { bookConstants } from '@/constants';
import { BookDate } from '@/types/state/book';
import { CampaignService, Place, PlaceService } from '@/types/state/shared';
import { formatPrice } from './numbers';
import {
  getDynamicPriceKey,
  getOfferPrice,
  getServiceRangePrice,
  hasDynamicPricing,
  showPrices,
  sumUp,
} from './placeHelper';

export function formatServicePrice(price: string, campaignService: CampaignService) {
  if (!campaignService) {
    return price
      .split('-')
      .map((item) => item.replace(/\s+/g, ''))
      .map((price) => parseInt(price, 10));
  }

  const offerPrice = getOfferPrice(price, campaignService);
  if (offerPrice.indexOf('-') === -1) {
    return [parseInt(offerPrice.replace(/\s+/g, ''), 10)];
  }
  return offerPrice
    .split('-')
    .map((item) => item.replace(/\s+/g, ''))
    .map((price) => parseInt(price, 10));
}

export function getUnformattedServicePrice(
  service: PlaceService,
  priceId: string,
  place: Place,
  campaignService?: CampaignService,
  time?: BookDate,
): { value: number[]; type: 'from' | 'variable' | 'free' | 'range' | 'standard' } {
  if (!(service && service.priceType && service.priceType.prices) || !(place && place.about)) {
    return undefined;
  }

  if (!showPrices(place, [service])) {
    return undefined;
  }

  const { about } = service;
  const { settings = {} } = about || {};

  // free service
  if (place.source === 2 && settings.isChargeable && settings.isFree) {
    return { value: [0], type: 'free' };
  }

  const hasDynamicPrice = hasDynamicPricing(service);

  if (priceId && priceId in service.priceType.prices) {
    if (!hasDynamicPrice || time?.dynamicPriceListIdKey === bookConstants.DYNAMIC_PRICE_KEY_DEFAULT) {
      const price = formatPrice(service.priceType.prices[priceId]);

      if (parseInt(price, 10) === 0) {
        return settings.isChargeable ? { value: [0], type: 'variable' } : { value: [0], type: 'free' };
      } else {
        return {
          value: formatServicePrice(price, campaignService),
          type: settings.showFrom ? 'from' : 'standard',
        };
      }
    } else {
      // the time chosen has weekend/evening price
      const dynamicPriceKey = getDynamicPriceKey(priceId, time?.dynamicPriceListIdKey);
      if (dynamicPriceKey && dynamicPriceKey in service.priceType.prices) {
        const price = formatPrice(service.priceType.prices[dynamicPriceKey]);

        if (parseInt(price, 10) === 0) {
          return settings.isChargeable ? { value: [0], type: 'variable' } : { value: [0], type: 'free' };
        } else {
          return {
            value: formatServicePrice(price, campaignService),
            type: settings.showFrom ? 'from' : 'standard',
          };
        }
      }
    }
  }

  // is a range probably
  if (Object.keys(service.priceType.prices).length > 0) {
    const serviceRange = getServiceRangePrice(service.priceType.prices, priceId, hasDynamicPrice ? time : undefined);
    const priceCheck = typeof serviceRange === 'string' ? parseInt(serviceRange, 10) : serviceRange;
    if (isNaN(priceCheck)) {
      return settings.isChargeable ? { value: [0], type: 'variable' } : { value: [0], type: 'free' };
    }

    const unformattedRange = sumUp(serviceRange, 0);
    // price formatted
    const range = ('' + unformattedRange).split('-').map((item) => formatPrice(item));

    // if it's a from price do not return a range
    if (!!settings.showFrom) {
      return { value: formatServicePrice(range[0], campaignService), type: 'from' };
    }

    return {
      value: formatServicePrice(range.join('-'), campaignService),
      type: range.length > 1 ? 'range' : 'standard',
    };
  } else {
    // check service.price
    const price = formatPrice(service.price);

    if (parseInt(price, 10) === 0) {
      return settings.isChargeable ? { value: [0], type: 'variable' } : { value: [0], type: 'free' };
    } else {
      return {
        value: formatServicePrice(price, campaignService),
        type: settings.showFrom ? 'from' : 'standard',
      };
    }
  }
}
